/** 시험 안내(면허취득절차 등) 본문용 정적 데이터 */

export interface ExamNoticeProcessStep {
  /** 1-based step index (표시용 "Step 01") */
  step: number;
  title: string;
  /** 본문 줄; `-` 로 시작하면 불릿, 아니면 이전 항목 이어쓰기(들여쓰기) */
  lines: readonly string[];
  /** Step 02 등 보조 안내 (`*` 스타일) */
  footnote?: string;
}

export interface WrittenExamLicenseEntity {
  sequence: number;
  licenseType: string;
  subLicenseType?: string;
  iconUrl: string;
  passingCriteria: string; // 합격기준
  examEligibility: string; // 시험자격
  fee: number; // 수수료
}

export const EXAM_PROCESS_NOTICE_STEPS: readonly ExamNoticeProcessStep[] = [
  {
    step: 1,
    title: '응시 전 교통안전 교육',
    lines: [
      '- 학과 시험 전까지 이수 완료',
      '- 준비물 : 신분증, 학과시험 전까지 이수',
    ],
  },
  {
    step: 2,
    title: '신체검사',
    lines: ['- 시험장 내 신체 검사실 또는 병원에서 검사 진행'],
    footnote: '* 문경, 강릉 태백시험장 내 신체검사원 없음',
  },
  {
    step: 3,
    title: '학과 접수',
    lines: [
      '- 준비물 : 응시원서, 신분증, 6개월 이내 촬영한 컬러사진(3.5 x 4.5cm) 3매',
    ],
  },
  {
    step: 4,
    title: '학과 시험',
    lines: ['- 불합격 시 : 불합격 다음날부터 재응시 가능'],
  },
  {
    step: 5,
    title: '기능 접수',
    lines: [
      '- 준비물 : 응시원서, 신분증',
      '- 대리접수 : 대리인 신분증 및 위임자의 위임장',
    ],
  },
  {
    step: 6,
    title: '기능 시험',
    lines: ['- 불합격 시 : 불합격일로부터 3일 경과 후 재응시 가능'],
  },
  {
    step: 7,
    title: '연습면허 발급',
    lines: [
      '- 제 1·2종 보통면허시험 응시자로, 학과시험 및 장내기능 시험에 모두 합격한 자',
    ],
  },
  {
    step: 8,
    title: '도로주행 접수',
    lines: [
      '- 준비물 : 응시원서(연습면허 부착), 신분증',
      '- 대리접수 : 대리인의 신분증 및 위임자의 위임장',
    ],
  },
  {
    step: 9,
    title: '도로주행 시험',
    lines: ['- 불합격 시 : 불합격일로부터 3일 경과 후 재응시 가능'],
  },
  {
    step: 10,
    title: '운전면허증 발급',
    lines: [
      '- 제 1·2종 보통면허 : 연습면허 취득 후 도로주행 시험에 합격한 자',
      '- 기타면허 : 학과시험, 기능시험에 합격한 자',
    ],
  },
] as const;

export type SkillCourseTextPart =
  | string
  | {
      readonly text: string;
      readonly emphasis?: 'bold' | 'extrabold';
    };

/** 구간 강조가 가능한 한 줄 텍스트 */
export type SkillCourseRichLine = string | readonly SkillCourseTextPart[];

/**
 * 모델 1.
 * [step 숫자] 제목
 * 내용(강조 구간 가능)
 */
export interface SkillCourseIntroSection {
  readonly stepLabel: string;
  readonly title: string;
  readonly content: SkillCourseRichLine;
  /**
   * 내용 아래에 들어가는 이미지(선택).
   * (width 기준 높이 산정을 위해 aspect 사용)
   */
  readonly imageBelowSrc?: string;
  readonly imageBelowAlt?: string;
  readonly imageBelowAspect?: readonly [number, number];
  /** intro 하단 이미지 클릭 시 재생할 영상 URL. 비어 있으면 클릭해도 동영상 뷰어를 열지 않음 */
  readonly imageBelowVideoUrl?: string;
}

/**
 * 모델 2.
 * 이미지(optional, 제목 위)
 * 제목
 * 이미지(optional, 제목 아래)
 * 내용(optional)
 * 상세내용(회색박스, optional)
 */
export interface SkillCourseDetailSection {
  readonly title: string;
  /** 섹션 상단 구분선 */
  readonly dividerTop?: boolean;
  /** 섹션 하단 구분선 */
  readonly dividerBottom?: boolean;
  readonly imageTopSrc?: string;
  readonly imageTopAlt?: string;
  /** 이미지 비율(가로:세로). width 기준 높이 산정용 */
  readonly imageTopAspect?: readonly [number, number];
  /** 상단 이미지 클릭 시 재생할 영상 URL. 비어 있으면 클릭해도 동영상 뷰어를 열지 않음 */
  readonly imageTopVideoUrl?: string;
  readonly imageBottomSrc?: string;
  readonly imageBottomAlt?: string;
  /** 이미지 비율(가로:세로). width 기준 높이 산정용 */
  readonly imageBottomAspect?: readonly [number, number];
  /** 하단 이미지 클릭 시 재생할 영상 URL. 비어 있으면 클릭해도 동영상 뷰어를 열지 않음 */
  readonly imageBottomVideoUrl?: string;
  readonly content?: SkillCourseRichLine;
  readonly detailBox?: {
    readonly content: SkillCourseRichLine;
  };
  /**
   * detailBox와 같은 레벨에 표시되는 참고사항(선택).
   * detailBox가 있는 경우, detailBox 아래에 이어서 렌더링합니다.
   */
  readonly etc?: SkillCourseRichLine;
}

/** 시험 코스 및 항목 3depth 공통 본문 모델 */
export interface SkillCourseGuideModel {
  readonly intro: SkillCourseIntroSection;
  readonly sections: readonly SkillCourseDetailSection[];
}

/** 장내기능시험 > 시험 코스 및 항목 > 장치조작 및 출발 */
export const SKILL_COURSE_START_GUIDE: SkillCourseGuideModel = {
  intro: {
    stepLabel: 'Step 02',
    title: '장치 조작 및 출발',
    content:
      '기능시험 시작 직후 진행되는 단계로, 차량 기본 장치 조작능력과 출발 절차를 확인하는 구간입니다. 안내음성에 따라 각 장치를 정해진 시간 내에 정확하게 조작하는 것이 중요합니다.',
  },
  sections: [
    {
      title: '시동걸기',
      // imageTopSrc: '/images/exam/skill/skill_step2_1.webp',
      imageTopSrc: '/images/exam/skill/skill_course_start1.gif',
      imageTopAspect: [360, 203],
      imageTopAlt: '시동걸기',
      dividerTop: true,
      content:
        '시험 시작 후 가장 먼저 진행되는 항목으로 차량 시동을 정확하게 거는 항목입니다.',
      detailBox: {
        content: [
          '- 브레이크를 밟은 상태에서 시동을 걸어야함',
          '- 안내 음성 후 5초 이내에 시동을 완료해야함',
        ],
      },
    },
    {
      title: '장치 조작 평가',
      dividerTop: true,
      content:
        '시동 이후에는 차량 기본 장치 조작 능력을 확인하는 평가가 진행됩니다. 기어변속/ 방향지시등 / 전조등 / 와이퍼 등 2가지 항목이 무작위로 제시되며 각 항목은 안내 음성 후 정해진 시간 내에 정확하게 조작 해야합니다.',
    },
    {
      title: '1. 기어변속기 조작',
      imageTopSrc: '/images/exam/skill/skill_step2_2.webp',
      imageTopAlt: '기어변속기 조작',
      imageTopVideoUrl:
        'https://file.drivingplus.me/file/2026/0508/45379-skill_course_start2.mp4',
      detailBox: {
        content: [
          '- 안내 후 10초 이내에 조작',
          '- 브레이크를 밟은 상태에서 진행',
          '- N(중립) → P(주차) 순서로 변속',
        ],
      },
    },
    {
      title: '2. 전조등 조작',
      imageTopSrc: '/images/exam/skill/skill_step2_3.webp',
      imageTopAlt: '전조등 조작',
      imageTopVideoUrl:
        'https://file.drivingplus.me/file/2026/0507/45369-skill_course_start3.mp4',
      detailBox: {
        content: [
          '- 각 단계별 5초 이내 수행',
          '- 하향등 → 상향등 → 하향등 → 소등 순서 진행',
          '-  조작부 회전 및 밀기/당기기로 전환',
        ],
      },
    },
    {
      title: '3. 방향지시등 조작',
      imageTopSrc: '/images/exam/skill/skill_step2_4_1.webp',
      imageTopAlt: '방향지시등 조작',
      imageTopVideoUrl:
        'https://file.drivingplus.me/file/2026/0507/45370-skill_course_start4.mp4',
      detailBox: {
        content: [
          '- 안내 후 5초 이내에 조작',
          '- 레버 위 : 우측 점등 / 아래 : 좌측 점등',
          '- 조작 후 반드시 원위치로 복귀하여 소등',
        ],
      },
    },
    {
      title: '4. 와이퍼 조작',
      imageTopSrc: '/images/exam/skill/skill_step2_5_1.webp',
      imageTopAlt: '와이퍼 조작',
      imageTopVideoUrl:
        'https://file.drivingplus.me/file/2026/0507/45368-skill_course_start5.mp4',
      detailBox: {
        content: [
          '- 안내 후 5초 이내에 조작',
          '- 레버를 아래로 내리면 작동, 위로 올리면 정지',
        ],
      },
    },
    {
      title: '출발안내',
      imageBottomSrc: '/images/exam/skill/skill_step2_6.webp',
      imageBottomAlt: '출발안내',
      imageBottomVideoUrl:
        'https://file.drivingplus.me/file/2026/0507/45349-skill_course_start2.mp4',
      dividerTop: true,
      detailBox: {
        content: [
          '- 주차 브레이크(P)를 해제',
          '- 기어를 D(주행) 위치로 변경',
          '- 출발 시 좌측 방향지시등을 점등',
          '- 이후 20km/h 이하 속도를 유지하며 주행 시작',
        ],
      },
    },
  ],
};

/** 장내기능시험 > 시험 코스 및 항목 > 출발 전 준비(before) */
export const SKILL_COURSE_BEFORE_GUIDE: SkillCourseGuideModel = {
  intro: {
    stepLabel: 'Step 01',
    title: '출발 전 준비',
    content:
      '기능시험 시작 전, 올바른 운전 자세와 차량 조작 상태를 점검하는 단계입니다. 출발 전 기본 세팅을 정확하게 맞추는 것은 안정적인 주행과 감점 방지에 중요한 영향을 줍니다.',
  },
  sections: [
    {
      title: '승차 후 운전석 및 후사경 조정',
      imageTopSrc: '/images/exam/skill/skill_step1_1.webp',
      imageTopAlt: '승차 후 운전석 및 후사경 조정',
      imageTopVideoUrl:
        'https://file.drivingplus.me/file/2026/0507/45346-skill_course_before1.mp4',
      dividerTop: true,
      content:
        '운전석에 앉은 후, 본인의 체형에 맞게 앞뒤 위치를 조절합니다. 좌석이 너무 가깝거나 멀 경우 페달 조작이 불편해지고, 긴급 상황 시 대응이 늦어질 수 있습니다. 본인의 눈높이에 맞춰 후사경 및 사이드미러 또한 조절 합니다.',
    },
    {
      title: '안전벨트 착용',
      imageTopSrc: '/images/exam/skill/skill_step1_2.webp',
      imageTopAlt: '안전벨트 착용',
      imageTopVideoUrl:
        'https://file.drivingplus.me/file/2026/0507/45347-skill_course_before2.mp4',
      dividerTop: true,
      content:
        '시험 시작 전 반드시 안전벨트를 착용해야 합니다. 미착용 시 감점 또는 실격 처리될 수 있으므로, 출발 전 가장 먼저 확인해야 할 사항입니다.',
    },
    {
      title: '주의사항',
      dividerTop: true,
      content:
        '시험 중에는 안내 음성이 제공되며, 반드시 음성이 끝난 후 출발해야 합니다. 안내 전에 조작을 시작할 경우 감점요인이 될 수 있으므로, 충분한 여유를 가지고 침착하게 진행하는 것이 중요합니다.',
    },
  ],
};

/** 장내기능시험 > 시험 코스 및 항목 > 경사로(slope) */
export const SKILL_COURSE_SLOPE_GUIDE: SkillCourseGuideModel = {
  intro: {
    stepLabel: 'Step 03',
    title: '경사로',
    content:
      '출발 후 가장 먼저 진행하는 코스로, 경사 구간에서 정지 및 재출발 능력을 평가하는 단계 입니다. 정해진 위치에서 정확하게 정지하고, 일정 시간 유지한 뒤 출발해야 합니다.',
  },
  sections: [
    {
      title: '정지방법',
      imageTopSrc: '/images/exam/skill/skill_step3_1.webp',
      imageTopAlt: '정지방법',
      imageTopVideoUrl:
        'https://file.drivingplus.me/file/2026/0507/45348-skill_course_slope1.mp4',
      dividerTop: true,
      detailBox: {
        content: [
          '- 경사로 구간에는 두 개의 백색선이 표시',
          '- 차량이 이 두 선 사이에 위치하도록 정지',
          '- 정지 후 3초간 유지',
        ],
      },
    },
    {
      title: '정지 편차 및 출발',
      dividerTop: true,
      detailBox: {
        content: [
          '- 정지 상태가 인식되면 계기판 또는 화면에 경사로 통과표시와 함께 시간이 표시',
          '- 3초가 경과한 후 브레이크에서 발을 떼고 출발',
        ],
      },
    },
    {
      title: '주의사항',
      content:
        '- 경사로에서 차가 뒤로 밀리지 않게 브레이크를 세게 밟아주는 것이 안전합니다.',
    },
  ],
};

/** 장내기능시험 > 시험 코스 및 항목 > 교차로(intersection) */
export const SKILL_COURSE_INTERSECTION_GUIDE: SkillCourseGuideModel = {
  intro: {
    stepLabel: 'Step 04',
    title: '교차로',
    content:
      '장내기능시험 중 두 번 진행되는 코스로, 신호에 따라직진 또는 좌회전을 수행하는 구간입니다. 신호 판단과 차선 유지 방향지시등 사용이 핵심 평가 요소입니다.',
  },
  sections: [
    {
      title: '교차로(직진)',
      imageTopSrc: '/images/exam/skill/skill_step4_1.webp',
      imageTopAlt: '교차로(직진)',
      imageTopVideoUrl:
        'https://file.drivingplus.me/file/2026/0507/45350-skill_course_intersection1.mp4',
      dividerTop: true,
      content: '신호에 맞춰 교차로를 직진 통과하는 과정입니다.',
    },
    {
      title: '주행방법',
      imageBottomSrc: '/images/exam/skill/skill_step4_2.webp',
      imageBottomAlt: '주행방법',
      imageBottomAspect: [320, 267],
      imageBottomVideoUrl: '',
    },
    {
      title: '주의사항',
      content: [
        '- 정지 시 차량 앞부분이 정지선을 넘지 않도록 주의합니다.',
        '- 신호가 바뀌는 상황에서는 무리한 정지나 급가속을 피하고 안정적으로 대응합니다.',
      ],
    },
    {
      title: '교차로(좌회전)',
      imageTopSrc: '/images/exam/skill/skill_step4_3.webp',
      imageTopAlt: '교차로(좌회전)',
      imageTopVideoUrl:
        'https://file.drivingplus.me/file/2026/0507/45351-skill_course_intersection2.mp4',
      dividerTop: true,
      content:
        '신호에 따라 좌회전으로 교차로를 통과하는 과정입니다. 직진과 달리 방향지시등과 회전 궤적이 중요한 평가 요소입니다.',
    },
    {
      title: '주행방법',
      detailBox: {
        content: [
          '- 좌회전 신호를 확인한 후 출발',
          '- 출발 전 좌측 방향지시등을 반드시 점등',
          '- 속도를 줄이고 천천히 회전하며 교차로를 통과',
        ],
      },
    },
    {
      title: '차선 유지 요령',
      detailBox: {
        content: [
          '- 운전자의 기준이 아닌 차량 중심이 도로 중앙을 따라간다는 느낌으로 주행',
          '- 좌회전 시에는 차량이 안쪽으로 과하게 들어가지 않도록 완만한 곡선을 그리며 회전',
        ],
      },
    },
    {
      title: '주의사항',
      content: [
        '- 회전 중 차선을 이탈하지 않도록 주의해야 합니다.',
        '- 방향지시등을 켜져 않거나 늦게 켤 경우 감점될 수 있습니다.',
        '- 회전 후에는 방향지시등을 반드시 해제해야 합니다.',
      ],
    },
  ],
};

/** 장내기능시험 > 시험 코스 및 항목 > 직각주차(parking) */
export const SKILL_COURSE_PARKING_GUIDE: SkillCourseGuideModel = {
  intro: {
    stepLabel: 'Step 05',
    title: '직각주차',
    content:
      '장내기능시험 중 직각주차 코스로, 지정된 주차구역에 차량을 정확히 주차한 후 다시 출발하는 구간입니다. 차량 위치 판단과 핸들 조작, 후진 시 속도 조절이 핵심 평가 요소입니다.',
    imageBelowSrc: '/images/exam/skill/skill_step5_1.webp',
    imageBelowAlt: '직각주차 코스 안내',
    imageBelowAspect: [960, 693],
    imageBelowVideoUrl: '',
  },
  sections: [
    {
      title: '코스 진입',
      imageTopSrc: '/images/exam/skill/skill_step5_2.webp',
      imageTopAlt: '코스 진입',
      imageTopVideoUrl:
        'https://file.drivingplus.me/file/2026/0507/45352-skill_course_parking1.mp4',
      dividerTop: true,
      detailBox: {
        content: [
          '- 코스 입구에서 차량을 직선 상태로 정렬',
          '- 앞바퀴가 코너 입구에 들어가는 위치에서 정지',
          '- 차량이 한쪽으로 치우치지 않도록 중앙을 맞춤',
        ],
      },
    },
    {
      title: '진입 후 위치 맞추기',
      imageTopSrc: '/images/exam/skill/skill_step5_3.webp',
      imageTopAlt: '진입 후 위치 맞추기',
      imageTopVideoUrl:
        'https://file.drivingplus.me/file/2026/0507/45353-skill_course_parking2.mp4',
      dividerTop: true,
      detailBox: {
        content: [
          '- 천천히 전진하여 차량을 코스 안쪽으로 진입',
          '- 오른쪽 문과 노란선이 가까워지는 지점에서 정지',
          '- 이후 반대쪽 기준을 확인하며 위치를 맞춤',
        ],
      },
      etc: '* 사이드미러를 활용해 좌우 간격을 계속 확인하는 것이 중요합니다.',
    },
    {
      title: '후진 주차',
      imageTopSrc: '/images/exam/skill/skill_step5_4.webp',
      imageTopAlt: '후진 주차',
      imageTopVideoUrl:
        'https://file.drivingplus.me/file/2026/0507/45354-skill_course_parking3.mp4',
      dividerTop: true,
      detailBox: {
        content: [
          '- 기어를 R(후진)으로 변경',
          '- 핸들을 한 방향으로 끝까지 돌린 후 천천히 후진',
          '- 차량이 주차 공간과 평행해질 때까지 조향을 유지',
        ],
      },
      etc: '* 속도는 최대한 천천히 유지하고, 차량이 선을 넘지 않게 지속적으로 확인합니다.',
    },
    {
      title: '주차 위치 맞추기',
      imageTopSrc: '/images/exam/skill/skill_step5_5.webp',
      imageTopAlt: '주차 위치 맞추기',
      imageTopVideoUrl:
        'https://file.drivingplus.me/file/2026/0507/45355-skill_course_parking4.mp4',
      dividerTop: true,
      detailBox: {
        content: [
          '- 차량이 공간 안에 들어오면 잠시 정지 후 핸들을 풀어 정렬',
          '- 뒷바퀴가 기준선에 닿을 때까지 천천히 후진',
          '- 위치가 맞으면 정지 상태를 유지',
        ],
      },
      etc: '* 정확한 위치가 인식되면 안내 음성이 제공됩니다.',
    },
    {
      title: '출차',
      imageTopSrc: '/images/exam/skill/skill_step5_6.webp',
      imageTopAlt: '출차',
      imageTopVideoUrl:
        'https://file.drivingplus.me/file/2026/0507/45356-skill_course_parking5.mp4',
      dividerTop: true,
      detailBox: {
        content: [
          '- 주차 브레이크 해제 후 기어를 D(주행)로 변경',
          '- 천천히 전진하며 핸들을 반대 방향으로 끝까지 돌림',
          '- 코스를 벗어날 때까지 부드럽게 회전하며 진행',
        ],
      },
      etc: '* 출차 시에도 선을 밟지 않도록 주의하며, 처음 진입 때와 동일한 방식으로 빠져나오면 됩니다.',
    },
    {
      title: '주의사항',
      dividerTop: true,
      content: [
        '- 속도를 줄이고 천천히 조작하는것이 가장 중요합니다.',
        '- 좌우 사이드미러를 통해 차량 위치를 지속적으로 확인합니다.',
        '- 시간에 쫓기기보다는 정확한 위치를 맞추기는 것에 집중하는것이 중요합니다.',
      ],
    },
  ],
};

/** 장내기능시험 > 시험 코스 및 항목 > 가속구간(curve) */
export const SKILL_COURSE_CURVE_GUIDE: SkillCourseGuideModel = {
  intro: {
    stepLabel: 'Step 06',
    title: '가속구간',
    content:
      '일정 구간에서 차량을 가속한 후 다시 감속하는 코스로 속도 조절 능력과 페달 조작의 안정성을 평가합니다.',
  },
  sections: [
    {
      title: '진입 및 가속',
      imageTopSrc: '/images/exam/skill/skill_step6_1.webp',
      imageTopAlt: '진입 및 가속',
      imageTopVideoUrl:
        'https://file.drivingplus.me/file/2026/0507/45357-skill_course_curve1.mp4',
      dividerTop: true,
      detailBox: {
        content: [
          '- 가속 구간에 진입하면 천천히 속도를 올림',
          '- 차량이 가속 구간 표지판을 지날 시점부터 시작',
          '- 20km/h 이상이 되도록 속도를 올려야 함',
        ],
      },
      etc: '* 급하게 밟기보다, 부드럽게 가속하여 자연스럽게 속도를 올리는 것이 중요합니다.',
    },
    {
      title: '속도 유지',
      imageTopSrc: '/images/exam/skill/skill_step6_2.webp',
      imageTopAlt: '속도 유지',
      imageTopVideoUrl:
        'https://file.drivingplus.me/file/2026/0507/45358-skill_course_curve2.mp4',
      dividerTop: true,
      detailBox: {
        content:
          '- 20km/h 이상 속도에 도달한 후 약 1초 정도 유지하면 안정적으로 통과',
      },
      etc: '* 짧게라도 속도를 확실히 넘겨주는 것이 중요합니다.',
    },
    {
      title: '감속 준비',
      imageTopSrc: '/images/exam/skill/skill_step6_3.webp',
      imageTopAlt: '감속 준비',
      imageTopVideoUrl:
        'https://file.drivingplus.me/file/2026/0507/45359-skill_course_curve3.mp4',
      dividerTop: true,
      detailBox: {
        content: [
          '- 구간 종료 표지판이 보이면 미리 감속 준비',
          '- 브레이크를 부드럽게 밟아 다시 20km/h 미만으로 감속',
        ],
      },
    },
    {
      title: '주의사항',
      dividerTop: true,
      content: [
        '- 급가속, 급제동은 감점 요인이 될 수 있습니다.',
        '- 속도를 올리지 못하거나 기준 속도에 도달하지 못하면 감점 됩니다.',
        '- 감속 시 차량이 완전히 정지하면 감점이 발생합니다.',
        '- 속도를 일정하게 조절하는 것이 중요합니다.',
      ],
    },
  ],
};

/** 장내기능시험 > 시험 코스 및 항목 > 돌발상황(emergency) */
export const SKILL_COURSE_EMERGENCY_GUIDE: SkillCourseGuideModel = {
  intro: {
    stepLabel: 'Step 07',
    title: '돌발상황',
    content:
      '주행 중 코스와 코스 사이에서 무작위로 발생하는 상황으로, 긴급 상황에 대한 대응능력을 평가하는 구간입니다.',
    imageBelowSrc: '/images/exam/skill/skill_step7_1.webp',
    imageBelowAlt: '돌발상황 코스 안내',
    imageBelowVideoUrl:
      'https://file.drivingplus.me/file/2026/0507/45364-skill_course_emergency1.mp4',
    imageBelowAspect: [960, 360],
  },
  sections: [
    {
      title: '발생 시 대응 방법',
      dividerTop: true,
      detailBox: {
        content: [
          '- 돌발 상황 안내 음성이 나오면 즉시 대응',
          '- 2초 이내 브레이크를 밟아 정지',
          '- 정지 후 3초 이내 비상등을 점등',
        ],
      },
    },
    {
      title: '상황 종료 후',
      imageTopSrc: '/images/exam/skill/skill_step7_2.webp',
      imageTopAlt: '상황 종료 후',
      imageTopVideoUrl:
        'https://file.drivingplus.me/file/2026/0507/45365-skill_course_emergency2.mp4',
      dividerTop: true,
      detailBox: {
        content: ['- 돌발 상황 종료 안내가 나오면 비상등을 끄고 다시 출발'],
      },
    },
    {
      title: '주의사항',
      dividerTop: true,
      etc: [
        '- 돌발 상황은 코스 진행 중이 아닌, 이동 구간에서만 발생합니다.',
        '- 정지 시간이 늦거나 비상등 점등이 지연될 경우 감점됩니다.',
        '- 급하게 핸들을 조작하기보다, 브레이크 -> 정지 -> 비상등 순서로 침착하게 대응하는 것이 중요합니다.',
      ],
    },
  ],
};

/** 장내기능시험 > 시험 코스 및 항목 > 종료방법(finish) */
export const SKILL_COURSE_FINISH_GUIDE: SkillCourseGuideModel = {
  intro: {
    stepLabel: 'Step 08',
    title: '종료',
    content:
      '모든 코스를 완료한 후, 종료 지점에서 시험을 마무리하는 단계이며, 마지막까지 정확한 절차를 지켜야 감점없이 시험을 종료할 수 있습니다.',
  },
  sections: [
    {
      title: '종료방법',
      imageTopSrc: '/images/exam/skill/skill_step8_1.webp',
      imageTopAlt: '종료방법',
      imageTopVideoUrl:
        'https://file.drivingplus.me/file/2026/0507/45361-skill_course_finish1.mp4',
      dividerTop: true,
      detailBox: {
        content: [
          '- 종료 표시가 있는 백색선을 통과하면 시험이 종료',
          '- 우측 방향지시등을 켠 상태로 통과',
        ],
      },
    },
    {
      title: '정차 및 마무리',
      imageTopSrc: '/images/exam/skill/skill_step8_2.webp',
      imageTopAlt: '정차 및 마무리',
      imageTopVideoUrl:
        'https://file.drivingplus.me/file/2026/0507/45362-skill_course_finish2.mp4',
      dividerTop: true,
      detailBox: {
        content: [
          '- 종료 지점을 지난 후 차량을 정차',
          '- 기어를 P(주차) 위치에 두기',
          '- 주차 브레이크를 체결',
          '- 이후 시동을 끄면 모든 시험이 종료',
        ],
      },
    },
    {
      title: '주의사항',
      dividerTop: true,
      etc: [
        '- 종료 구간에서도 방향지시등 미점등 시 감점될 수 있습니다.',
        '- 정차 후 기어 및 주차 브레이크 조작을 누락하지 않도록 주의해야 합니다.',
        '- 마지막 단계까지 긴장을 유지하고 정확하게 마무리하는 것이 중요합니다.',
      ],
    },
  ],
};

/** 장내기능시험 > 시험 코스 및 항목 > 단계 id (`?step=` 값) */
export type SkillCourseStepId =
  | 'before'
  | 'start'
  | 'slope'
  | 'intersection'
  | 'parking'
  | 'curve'
  | 'emergency'
  | 'finish';

/**
 * 장내기능시험 코스 8단계의 정본 순서.
 *
 * 도로주행과 달리 장내기능 코스 순서는 전 시험장·학원이 동일해서, 이 목록 하나가
 * "장내기능시험 순서" 질의의 유일한 정답 소스가 된다. 시험장·학원 상세에 같은 내용을
 * 복제하지 말고 이 페이지로 링크할 것 — 수백 개 상세가 동일 문단을 갖게 되면
 * 근사중복으로 상세 페이지의 고유성(도로주행 코스)이 희석된다.
 *
 * id·순서는 `?type=skill&sub=course&step=…`, `EXAM_NOTICE_MENU`,
 * `next-sitemap.config.js`의 `skill.course.steps`와 반드시 일치해야 한다.
 */
export const SKILL_COURSE_STEP_SEQUENCE: readonly {
  readonly id: SkillCourseStepId;
  readonly guide: SkillCourseGuideModel;
}[] = [
  { id: 'before', guide: SKILL_COURSE_BEFORE_GUIDE },
  { id: 'start', guide: SKILL_COURSE_START_GUIDE },
  { id: 'slope', guide: SKILL_COURSE_SLOPE_GUIDE },
  { id: 'intersection', guide: SKILL_COURSE_INTERSECTION_GUIDE },
  { id: 'parking', guide: SKILL_COURSE_PARKING_GUIDE },
  { id: 'curve', guide: SKILL_COURSE_CURVE_GUIDE },
  { id: 'emergency', guide: SKILL_COURSE_EMERGENCY_GUIDE },
  { id: 'finish', guide: SKILL_COURSE_FINISH_GUIDE },
] as const;

/**
 * 8단계를 한 줄로 이은 순서 문장.
 * 화면(코스 안내 상단)과 HowTo `description`에 같은 문자열을 쓴다 — 마크업에만 있고
 * 화면에 없는 정보는 구조화 데이터 정책 위반이다.
 */
export const SKILL_COURSE_SEQUENCE_SUMMARY = SKILL_COURSE_STEP_SEQUENCE.map(
  (s) => s.guide.intro.title,
).join(' → ');

/** 학과시험 > 시험 안내  */
export interface WrittenNoticeSummaryItem {
  iconUrl: string;
  title: string;
  content: string;
  subContent?: WrittenNoticeSummaryItem[];
}

export const WRITTEN_NOTICE_SUMMARY: readonly WrittenNoticeSummaryItem[] = [
  {
    iconUrl: '/images/exam/written/icon_pass_standard.svg',
    title: '합격기준',
    content: '1종 70점 · 2종 60점',
  },
  {
    iconUrl: '/images/exam/written/icon_timer.svg',
    title: '시험시간',
    content: '40분',
  },
  {
    iconUrl: '/images/exam/written/icon_type.svg',
    title: '시험유형',
    content: '객관식(선다형)',
    subContent: [
      {
        iconUrl: '/images/exam/written/icon_type_sentence.svg',
        title: '문장형',
        content: '21문제',
      },
      {
        iconUrl: '/images/exam/written/icon_type_picture.svg',
        title: '사진형',
        content: '6문제',
      },
      {
        iconUrl: '/images/exam/written/icon_type_illustration.svg',
        title: '일러스트형',
        content: '7문제',
      },
      {
        iconUrl: '/images/exam/written/icon_type_safety_sign.svg',
        title: '안전표지형',
        content: '5문제',
      },
      {
        iconUrl: '/images/exam/written/icon_type_movie.svg',
        title: '동영상형',
        content: '1문제',
      },
    ],
  },
  {
    iconUrl: '/images/exam/written/icon_language.svg',
    title: '응시 가능 언어',
    content: '한국어, 영어, 중국어, 베트남어',
  },
] as const;

interface WrittenNoticeCommonLine {
  content: string;
  subContent?: string;
}

/** 학과시험 > 시험 안내 — 본문 블록(drivingzone THEORY_EXAM_COMMON_LIST) */
export interface WrittenNoticeCommonSection {
  title: string;
  content: readonly WrittenNoticeCommonLine[];
  highlight?: readonly string[];
  prevIconUrl?: string;
  prevChar?: string;
}

export type SkillNoticeDepth3Id =
  'normal' | 'large' | 'special' | 'small' | 'motor';

/** 장내기능시험 > 시험 안내 > 1·2종 보통 — 상단 요약 */
export const SKILL_NOTICE_NORMAL_SUMMARY: readonly WrittenNoticeSummaryItem[] =
  [
    {
      iconUrl: '/images/exam/skill/icon_license.svg',
      title: '면허종별',
      content: '1·2종 보통(수동/자동)',
    },
    {
      iconUrl: '/images/exam/skill/icon_pass_standard.svg',
      title: '합격기준',
      content: '80점 이상 시 합격',
    },
    {
      iconUrl: '/images/exam/skill/icon_fee.svg',
      title: '수수료',
      content: '25,000원',
    },
  ] as const;

/** 장내기능시험 > 시험 안내 > 1·2종 보통 — 시험코스 다이어그램 하단 캡션 */
export const SKILL_NOTICE_NORMAL_COURSE_FLOW_CAPTION =
  // '장치 조작 및 출발 → 경사로 → 교차로 → 직각주차(T자) → 가속구간 → 돌발상황 → 종료방법';
  [
    '- 주행거리 300m 이상, 운전장치 조작, 차로 준수·급정지, 경사로',
    '- 좌·우회전, 직각 주차, 신호교차로, 전진(가속구간)',
  ];

export const SKILL_NOTICE_NORMAL_YOUTUBE_VIDEO_ID = '4wrCY-DQ02k' as const;

/** 시험안내 동영상 카드(재사용 가능) */
export interface ExamNoticeVideoItem {
  readonly title: string;
  readonly caption?: string;
  readonly youtubeVideoId?: string;
  readonly videoUrl?: string;
}

export const SKILL_NOTICE_NORMAL_VIDEOS: readonly ExamNoticeVideoItem[] = [
  {
    title: '시험안내 동영상',
    caption: '1･2종보통 장내기능 시험안내 동영상(2025. 8. 25.)',
    youtubeVideoId: SKILL_NOTICE_NORMAL_YOUTUBE_VIDEO_ID,
  },
] as const;

export const SKILL_NOTICE_NORMAL_PREP_SECTION: WrittenNoticeCommonSection = {
  title: '준비물',
  content: [
    {
      content:
        '온라인 접수 또는 현장 예약 접수 후 시험 당일 응시원서, 신분증 지참',
    },
  ],
  prevChar: '-',
};

export const SKILL_NOTICE_NORMAL_DISQUALIFY_SECTION: WrittenNoticeCommonSection =
  {
    title: '실격기준',
    content: [
      {
        content:
          '점검이 시작될 때부터 종료될 때까지 좌석 안전띠를 착용하지 아니한 때',
      },
      {
        content:
          '시험 중 안전사고를 일으키거나 차의 바퀴 어느 하나라도 연석을 접촉한 때',
      },
      {
        content:
          '시험관의 지시나 통제를 따르지 않거나 음주, 과로, 마약·대마 등 약물 등의 영향으로 정상적인 시험 진행이 어려운 때',
      },
      {
        content:
          '특별한 사유 없이 출발 지시 후 출발선에서 30초 이내 출발하지 못한 때',
      },
      {
        content:
          '각 시험 코스를 어느 하나라도 시도하지 않거나 제대로 이행하지 않을 때(예: 경사로에서 정지하지 않고 통과, 직각 주차에서 차고에 진입해서 확인선을 접촉하지 않음, 가속코스에서 기어변속을 하지 않음)',
      },
      {
        content:
          '경사로 정지 구간 이행 후 30초를 초과하여 통과하지 못한 때 또는 경사로 정지 구간에서 후방으로 1미터 이상 밀린 때',
      },
      {
        content:
          '신호 교차로에서 신호위반을 하거나 또는 앞 범퍼가 정지선을 넘어간 때',
      },
    ],
    prevIconUrl: '/images/exam/skill/icon_red_x.svg',
  };

export const SKILL_NOTICE_NORMAL_CAUTION_SECTION: WrittenNoticeCommonSection = {
  title: '주의사항',
  content: [
    {
      content: '학과시험 합격일로부터 1년 이내 기능 시험에 합격하여야 함.',
    },
    {
      content:
        '1년경과 시 기존 원서 폐기 후 학과시험부터 신규 접수하여야 하며 이때 응시 전 교통안전교육 재수강은 불필요',
    },
  ],
  prevChar: '-',
};

export const SKILL_NOTICE_NORMAL_RETEST_SECTION: WrittenNoticeCommonSection = {
  title: '불합격 후 재응시',
  content: [
    {
      content:
        '기능시험 불합격자는(운전전문학원 검정 불합격 포함) 불합격 일로부터 3일 경과 후에 재 응시 가능',
      subContent:
        '예) 1일(월요일)에 불합격하였을 경우, 4일(목요일)부터 재응시 가능',
    },
  ],
  prevChar: '-',
};

export const SKILL_NOTICE_NORMAL_LANGUAGE_SUPPORT_SECTION: WrittenNoticeCommonSection =
  {
    title: '외국어 음성, 화면지원 서비스 제공',
    content: [
      {
        content: '영어, 중국어',
      },
    ],
    prevChar: '-',
  };

export interface SkillNoticeCourseSection {
  readonly title: string;
  readonly imageSrc?: string;
  readonly imageAlt?: string;
  readonly flowCaption: string | readonly string[];
  readonly ctaSubLabel?: string;
  readonly ctaLabel?: string;
  readonly ctaUrl?: string;
}

/** 장내기능시험 > 시험 안내 > depth3 공통 모델 */
export interface SkillNoticeContentModel {
  readonly summary: readonly WrittenNoticeSummaryItem[];
  readonly prepSection?: WrittenNoticeCommonSection;
  readonly courseSection?: SkillNoticeCourseSection;
  readonly disqualifySection?: WrittenNoticeCommonSection;
  readonly cautionSection?: WrittenNoticeCommonSection;
  readonly retestSection?: WrittenNoticeCommonSection;
  /** 불합격 후 재응시 섹션 바로 아래에 붙는 보조 섹션 */
  readonly sectionAfterRetest?: WrittenNoticeCommonSection;
  readonly videos: readonly ExamNoticeVideoItem[];
  /** 아직 데이터 미정인 depth3 메뉴용 안내 문구 */
  readonly placeholderMessage?: string;
}

export const SKILL_NOTICE_NORMAL_CONTENT: SkillNoticeContentModel = {
  summary: SKILL_NOTICE_NORMAL_SUMMARY,
  prepSection: SKILL_NOTICE_NORMAL_PREP_SECTION,
  courseSection: {
    title: '시험코스',
    imageSrc: '/images/exam/skill/skill_notice_class_1.webp',
    imageAlt: '장내기능시험 시험코스 안내',
    flowCaption: SKILL_NOTICE_NORMAL_COURSE_FLOW_CAPTION,
    ctaSubLabel: '기능시험 완전정복!',
    ctaLabel: '기능 시험코스 자세히 보러가기',
    ctaUrl: '/exam/notice?type=skill&sub=course&step=before',
  },
  disqualifySection: SKILL_NOTICE_NORMAL_DISQUALIFY_SECTION,
  cautionSection: SKILL_NOTICE_NORMAL_CAUTION_SECTION,
  retestSection: SKILL_NOTICE_NORMAL_RETEST_SECTION,
  sectionAfterRetest: SKILL_NOTICE_NORMAL_LANGUAGE_SUPPORT_SECTION,
  videos: SKILL_NOTICE_NORMAL_VIDEOS,
};

/** 장내기능시험 > 시험 안내 > 1종 대형 — 상단 요약 */
export const SKILL_NOTICE_LARGE_SUMMARY: readonly WrittenNoticeSummaryItem[] = [
  {
    iconUrl: '/images/exam/skill/icon_license.svg',
    title: '면허종별',
    content: '1종 대형',
  },
  {
    iconUrl: '/images/exam/skill/icon_pass_standard.svg',
    title: '합격기준',
    content: '80점 이상',
  },
  {
    iconUrl: '/images/exam/skill/icon_fee.svg',
    title: '수수료',
    content: '25,000원',
  },
] as const;

export const SKILL_NOTICE_LARGE_PREP_SECTION: WrittenNoticeCommonSection = {
  title: '준비물',
  content: [
    {
      content:
        '온라인 접수 또는 현장 예약 접수 후 시험 당일 응시원서, 신분증 지참',
    },
  ],
  prevChar: '-',
};

export const SKILL_NOTICE_LARGE_DISQUALIFY_SECTION: WrittenNoticeCommonSection =
  {
    title: '실격기준',
    content: [
      {
        content: '특별한 사유 없이 출발선에서 30초 이내 출발하지 못한 때',
      },
      {
        content:
          '경사로코스·굴절코스·방향전환코스·기어변속코스(자동변속기장치 자동차의 경우는 제외) 및 평행주차코스를 어느 하나라도 이행하지 아니한 때',
      },
      {
        content: '특별한 사유 없이 교차로 내에서 30초 이상 정차한 때',
      },
      {
        content: '안전사고를 일으키거나 단 1회라도 차로를 벗어난 때',
      },
      {
        content:
          '경사로 정지 구간 이행 후 30초를 초과하여 통과하지 못한 때 또는 경사로 정지 구간에서 후진하여 앞 범퍼가 경사로 사면을 벗어난 때',
      },
      {
        content:
          '시험이 시작될 때부터 종료될 때까지 좌석안전띠를 착용하지 않은 경우',
      },
    ],
    prevIconUrl: '/images/exam/skill/icon_red_x.svg',
  };

export const SKILL_NOTICE_LARGE_ELIGIBILITY_SECTION: WrittenNoticeCommonSection =
  {
    title: '응시자격',
    content: [
      {
        content: '19세 이상으로 제1∙2종 보통면허 취득 후 1년 이상인자',
      },
    ],
    prevChar: '-',
  };

export const SKILL_NOTICE_LARGE_RETEST_SECTION: WrittenNoticeCommonSection = {
  title: '불합격 후 재응시',
  content: [
    {
      content:
        '기능시험 불합격자는(운전전문학원 검정 불합격 포함) 불합격 일로부터 3일 경과 후에 재 응시 가능',
      subContent:
        '예) 1일(월요일)에 불합격하였을 경우, 4일(목요일)부터 재 응시 가능',
    },
  ],
  prevChar: '-',
};

export const SKILL_NOTICE_LARGE_CONTENT: SkillNoticeContentModel = {
  summary: SKILL_NOTICE_LARGE_SUMMARY,
  prepSection: SKILL_NOTICE_LARGE_PREP_SECTION,
  courseSection: {
    title: '시험코스',
    // imageSrc: '',
    // imageAlt: '1종 대형 시험코스 안내',
    flowCaption:
      '- 출발코스, 굴절코스, 곡선코스, 방향전환코스, 평행주차코스, 기어변속코스, 교통신호가 있는 십자형 교차로코스, 횡단보도코스, 철길건널목코스, 경사로코스, 종료코스',
    ctaSubLabel: '기능시험 완전정복!',
    // ctaLabel: '기능 시험코스 자세히 보러가기',
    // ctaUrl: '/exam/notice?type=skill&sub=course&step=before',
  },
  disqualifySection: SKILL_NOTICE_LARGE_DISQUALIFY_SECTION,
  cautionSection: SKILL_NOTICE_LARGE_ELIGIBILITY_SECTION,
  retestSection: SKILL_NOTICE_LARGE_RETEST_SECTION,
  videos: [
    {
      title: '시험안내 동영상',
      caption: '1종 대형 기능 시험안내(2026. 4. 24.)',
      youtubeVideoId: 'KTZoqel00hI',
    },
  ],
};

/** 장내기능시험 > 시험 안내 > 1종 특수 */
export const SKILL_NOTICE_SPECIAL_SUMMARY: readonly WrittenNoticeSummaryItem[] =
  [
    {
      iconUrl: '/images/exam/skill/icon_license.svg',
      title: '면허종별',
      content: '1종 특수',
    },
    {
      iconUrl: '/images/exam/skill/icon_pass_standard.svg',
      title: '합격기준',
      content: '90점 이상',
    },
    {
      iconUrl: '/images/exam/skill/icon_fee.svg',
      title: '수수료',
      content: '25,000원',
    },
  ] as const;

export const SKILL_NOTICE_SPECIAL_PREP_SECTION: WrittenNoticeCommonSection = {
  title: '준비물',
  content: [
    {
      content:
        '온라인 접수 또는 현장 예약 접수 후 시험 당일 응시원서, 신분증 지참',
    },
  ],
  prevChar: '-',
};

export const SKILL_NOTICE_SPECIAL_DISQUALIFY_SECTION: WrittenNoticeCommonSection =
  {
    title: '실격기준',
    content: [
      {
        content: '특별한 사유없이 20초 이내에 출발하지 못한 때',
      },
      {
        content: '어느 하나라도 이행하지 아니한 때',
      },
      {
        content: '시험 중 안전사고를 일으키거나 코스를 벗어난 때',
      },
      {
        content:
          '시험이 시작될 때부터 종료될 때까지 좌석안전띠를 착용하지 않은 경우',
      },
    ],
    prevIconUrl: '/images/exam/skill/icon_red_x.svg',
  };

export const SKILL_NOTICE_SPECIAL_ELIGIBILITY_SECTION: WrittenNoticeCommonSection =
  {
    title: '응시자격',
    content: [
      {
        content: '19세 이상으로 제1∙2종 보통면허 취득 후 1년 이상인자',
      },
    ],
    prevChar: '-',
  };

export const SKILL_NOTICE_SPECIAL_RETEST_SECTION: WrittenNoticeCommonSection = {
  title: '불합격 후 재 응시',
  content: [
    {
      content:
        '기능시험 불합격자는(운전전문학원 검정 불합격 포함) 불합격 일로부터 3일 경과 후에 재 응시 가능',
      subContent:
        '예) 1일(월요일)에 불합격하였을 경우, 4일(목요일)부터 재 응시 가능',
    },
  ],
  prevChar: '-',
};

export const SKILL_NOTICE_SPECIAL_CONTENT: SkillNoticeContentModel = {
  summary: SKILL_NOTICE_SPECIAL_SUMMARY,
  prepSection: SKILL_NOTICE_SPECIAL_PREP_SECTION,
  courseSection: {
    title: '시험코스',
    // imageSrc: '',
    // imageAlt: '1종 특수 시험코스 안내',
    flowCaption: [
      '- 대형 견인차 : 연결·코스·분리',
      '- 소형 견인차 : 굴절코스·곡선코스·방향전환코스',
      '- 구난차 : 굴절코스·곡선코스·방향전환코스',
    ],
    ctaSubLabel: '기능시험 완전정복!',
    // ctaLabel: '기능 시험코스 자세히 보러가기',
    // ctaUrl: '/exam/notice?type=skill&sub=course&step=before',
  },
  disqualifySection: SKILL_NOTICE_SPECIAL_DISQUALIFY_SECTION,
  cautionSection: SKILL_NOTICE_SPECIAL_ELIGIBILITY_SECTION,
  retestSection: SKILL_NOTICE_SPECIAL_RETEST_SECTION,
  videos: [
    {
      title: '시험안내 동영상',
      caption: '1종 특수 소형견인차 기능 시험안내 (2026. 4. 24.)',
      youtubeVideoId: 'GraMBV-clP0',
    },
    {
      title: '시험안내 동영상',
      caption: '1종 특수 대형견인차 기능 시험안내 (2026. 4. 24.)',
      youtubeVideoId: 'wxMoJtM1SxM',
    },
    {
      title: '시험안내 동영상',
      caption: '1종 특수 구난차 기능 시험안내 (2026. 4. 24.)',
      youtubeVideoId: 'y2MKCZMspME',
    },
  ],
};

/** 장내기능시험 > 시험 안내 > 2종 소형 */
export const SKILL_NOTICE_SMALL_SUMMARY: readonly WrittenNoticeSummaryItem[] = [
  {
    iconUrl: '/images/exam/skill/icon_license.svg',
    title: '면허종별',
    content: '2종 소형(125cc 초과 이륜자동차)',
  },
  {
    iconUrl: '/images/exam/skill/icon_pass_standard.svg',
    title: '합격기준',
    content: '90점 이상',
  },
  {
    iconUrl: '/images/exam/skill/icon_fee.svg',
    title: '수수료',
    content: '14,000원',
  },
] as const;

export const SKILL_NOTICE_SMALL_PREP_SECTION: WrittenNoticeCommonSection = {
  title: '준비물',
  content: [
    {
      content:
        '온라인 접수 또는 현장 예약 접수 후 시험 당일 응시원서, 신분증 지참',
    },
  ],
  prevChar: '-',
};

export const SKILL_NOTICE_SMALL_DISQUALIFY_SECTION: WrittenNoticeCommonSection =
  {
    title: '실격기준',
    content: [
      {
        content: '운전미숙으로 20초 이내에 출발하지 못한 때',
      },
      {
        content: '시험 과제를 하나라도 이행하지 아니한 때',
      },
      {
        content: '시험 중 안전사고를 일으키거나 코스를 벗어난 때',
      },
      {
        content:
          '시험이 시작될 때부터 종료될 때까지 제32조에 따른 인명보호장구를 착용하지 않은 경우',
      },
    ],
    prevIconUrl: '/images/exam/skill/icon_red_x.svg',
  };

export const SKILL_NOTICE_SMALL_RETEST_SECTION: WrittenNoticeCommonSection = {
  title: '불합격 후 재 응시',
  content: [
    {
      content:
        '기능시험 불합격자는(운전전문학원 검정 불합격 포함) 불합격 일로부터 3일 경과 후에 재 응시 가능',
      subContent:
        '예) 1일(월요일)에 불합격하였을 경우, 4일(목요일)부터 재 응시 가능',
    },
  ],
  prevChar: '-',
};

export const SKILL_NOTICE_SMALL_CONTENT: SkillNoticeContentModel = {
  summary: SKILL_NOTICE_SMALL_SUMMARY,
  prepSection: SKILL_NOTICE_SMALL_PREP_SECTION,
  courseSection: {
    title: '시험코스',
    // imageSrc: '',
    // imageAlt: '2종 소형 시험코스 안내',
    flowCaption: '- 굴절코스, 곡선코스, 좁은길코스, 연속진로전환코스',
    ctaSubLabel: '기능시험 완전정복!',
    // ctaLabel: '기능 시험코스 자세히 보러가기',
    // ctaUrl: '/exam/notice?type=skill&sub=course&step=before',
  },
  disqualifySection: SKILL_NOTICE_SMALL_DISQUALIFY_SECTION,
  retestSection: SKILL_NOTICE_SMALL_RETEST_SECTION,
  videos: [
    {
      title: '시험안내 동영상',
      caption: '2종 이륜차 기능 시험안내(2026. 4. 24.)',
      youtubeVideoId: 'DauxYMthkr8',
    },
  ],
};

/** 장내기능시험 > 시험 안내 > 2종 원동기 */
export const SKILL_NOTICE_MOTOR_SUMMARY: readonly WrittenNoticeSummaryItem[] = [
  {
    iconUrl: '/images/exam/skill/icon_license.svg',
    title: '면허종별',
    content: '2종 원동기장치 자전거',
  },
  {
    iconUrl: '/images/exam/skill/icon_pass_standard.svg',
    title: '합격기준',
    content: '90점 이상',
  },
  {
    iconUrl: '/images/exam/skill/icon_fee.svg',
    title: '수수료',
    content: '10,000원',
  },
] as const;

export const SKILL_NOTICE_MOTOR_PREP_SECTION: WrittenNoticeCommonSection = {
  title: '준비물',
  content: [
    {
      content:
        '온라인 접수 또는 현장 예약 접수 후 시험 당일 응시원서, 신분증 지참',
    },
  ],
  prevChar: '-',
};

export const SKILL_NOTICE_MOTOR_DISQUALIFY_SECTION: WrittenNoticeCommonSection =
  {
    title: '실격기준',
    content: [
      {
        content: '운전미숙으로 20초 이내에 출발하지 못한 때',
      },
      {
        content: '시험 과제를 하나라도 이행하지 아니한 때',
      },
      {
        content: '시험 중 안전사고를 일으키거나 코스를 벗어난 때',
      },
      {
        content:
          '시험이 시작될 때부터 종료될 때까지 제32조에 따른 인명보호장구를 착용하지 않은 경우',
      },
    ],
    prevIconUrl: '/images/exam/skill/icon_red_x.svg',
  };

export const SKILL_NOTICE_MOTOR_RETEST_SECTION: WrittenNoticeCommonSection = {
  title: '불합격 후 재 응시',
  content: [
    {
      content:
        '기능시험 불합격자는(운전전문학원 검정 불합격 포함) 불합격 일로부터 3일 경과 후에 재 응시 가능',
      subContent:
        '예) 1일(월요일)에 불합격하였을 경우, 4일(목요일)부터 재 응시 가능',
    },
  ],
  prevChar: '-',
};

export const SKILL_NOTICE_MOTOR_CONTENT: SkillNoticeContentModel = {
  summary: SKILL_NOTICE_MOTOR_SUMMARY,
  prepSection: SKILL_NOTICE_MOTOR_PREP_SECTION,
  courseSection: {
    title: '시험코스',
    // imageSrc: '',
    // imageAlt: '2종 원동기장치 자전거 시험코스 안내',
    flowCaption: [
      '- 굴절코스, 곡선코스, 좁은길코스, 연속진로전환코스',
      '※ 다륜형원동기장치자전거 굴절코스, 곡선코스만 진행',
    ],
    ctaSubLabel: '기능시험 완전정복!',
    // ctaLabel: '기능 시험코스 자세히 보러가기',
    // ctaUrl: '/exam/notice?type=skill&sub=course&step=before',
  },
  disqualifySection: SKILL_NOTICE_MOTOR_DISQUALIFY_SECTION,
  retestSection: SKILL_NOTICE_MOTOR_RETEST_SECTION,
  videos: [
    {
      title: '시험안내 동영상',
      caption: '2종 이륜차 기능 시험안내(2026. 4. 24.)',
      youtubeVideoId: 'DauxYMthkr8',
    },
  ],
};

// function createSkillNoticePlaceholderModel(
//   label: string,
// ): SkillNoticeContentModel {
//   return {
//     summary: [],
//     videos: [],
//     placeholderMessage: `${label} 안내 데이터는 준비 중입니다.`,
//   };
// }

/** 장내기능시험 > 시험 안내 > depth3별 본문 데이터(유지보수용 단일 진입점) */
export const SKILL_NOTICE_CONTENT_BY_DEPTH3: Record<
  SkillNoticeDepth3Id,
  SkillNoticeContentModel
> = {
  normal: SKILL_NOTICE_NORMAL_CONTENT,
  large: SKILL_NOTICE_LARGE_CONTENT,
  special: SKILL_NOTICE_SPECIAL_CONTENT,
  small: SKILL_NOTICE_SMALL_CONTENT,
  motor: SKILL_NOTICE_MOTOR_CONTENT,
};

/** 도로주행시험 > 시험 안내 — 상단 요약 */
export const ROAD_NOTICE_SUMMARY: readonly WrittenNoticeSummaryItem[] = [
  {
    iconUrl: '/images/exam/skill/icon_license.svg',
    title: '면허종별',
    content: '1종 보통1ㆍ2종 보통(수동/자동)',
  },
  {
    iconUrl: '/images/exam/skill/icon_pass_standard.svg',
    title: '합격기준',
    content: '70점 이상 시 합격',
  },
  {
    iconUrl: '/images/exam/skill/icon_fee.svg',
    title: '수수료',
    content: '30,000원',
  },
] as const;

export const ROAD_NOTICE_PREP_SECTION: WrittenNoticeCommonSection = {
  title: '준비물',
  content: [
    {
      content: '온라인 접수 또는 현장 예약 접수',
    },
    {
      content: '시험 당일 연습면허 발급·부착된 응시원서, 신분증 지참',
    },
  ],
  prevChar: '-',
};

export const ROAD_NOTICE_COURSE_SECTION: SkillNoticeCourseSection = {
  title: '시험 코스',
  imageSrc: '/images/exam/road/road_notice_class_1.webp',
  imageAlt: '도로주행시험 코스 안내',
  flowCaption: [
    '- 총 연장거리 5km 이상인 4개 코스 중 추첨을 통한 1개 코스 선택',
    '- 내비게이션 음성 길 안내',
  ],
};

export const ROAD_NOTICE_TEST_CONTENT_SECTION: WrittenNoticeCommonSection = {
  title: '시험 내용',
  content: [
    {
      content:
        '긴급자동차 양보, 어린이보호구역, 지정 속도 위반 등 안전운전에 필요한 57개 항목을 평가',
    },
  ],
  highlight: ['57개 항목을 평가'],
  prevChar: '-',
};

export const ROAD_NOTICE_DISQUALIFY_SECTION: WrittenNoticeCommonSection = {
  title: '실격기준',
  content: [
    {
      content:
        '3회 이상 "출발 불능", "클러치 조작 불량으로 인한 엔진 정지", "급브레이크 사용", "급조작·급출발" 또는 그 밖의 사유로 운전능력이 현저하게 부족한 것으로 인정할 수 있는 행위를 한 경우',
    },
    {
      content:
        '안전거리 미확보나 경사로에서 뒤로 1미터 이상 밀리는 현상 등 운전능력 부족으로 교통사고를 일으킬 위험이 현저한 경우 또는 교통사고를 야기한 경우',
    },
    {
      content:
        '음주, 과로, 마약, 대마 등 약물의 영향 이나 휴대전화 사용 등으로 정상적으로 운전하지 못할 우려가 있거나 교통안전과 소통을 위한 시험관의 지시 및 통제에 불응한 경우',
    },
    {
      content: '신호 또는 지시에 따르지 않은 경우',
    },
    {
      content: '보행자 보호의무 등을 소홀히 한 경우',
    },
    {
      content:
        '어린이 보호구역, 노인 및 장애인 보호구역에 지정되어 있는 최고 속도를 초과한 경우',
    },
    {
      content:
        '도로의 중앙으로부터 우측 부분을 통행하여야 할 의무를 위반한 경우',
    },
    {
      content:
        '법령 또는 안전표지 등으로 지정되어 있는 최고 속도를 시속 10킬로미터 초과한 경우',
    },
    {
      content:
        '긴급자동차의 우선통행 시 일시정지하거나 진로를 양보하지 않은 경우',
    },
    {
      content: '어린이 통학버스의 특별보호의무를 위반한 경우',
    },
    {
      content: '시험시간 동안 좌석안전띠를 착용하지 않은 경우',
    },
  ],
  prevIconUrl: '/images/exam/skill/icon_red_x.svg',
};

export const ROAD_NOTICE_CAUTION_SECTION: WrittenNoticeCommonSection = {
  title: '주의사항',
  content: [
    {
      content:
        '연습운전면허 유효기간(발급일로부터 1년) 이내 도로주행시험에 합격하여야 함.',
    },
    {
      content:
        '연습운전면허 유효기간이 지났을 경우, 도로주행시험 접수가 불가능하며 학과시험, 기능 시험에 재응시하여야 함. (응시 전 교통안전교육은 재수강 불필요)',
    },
  ],
  prevChar: '-',
};

export const ROAD_NOTICE_LANGUAGE_SUPPORT_SECTION: WrittenNoticeCommonSection =
  {
    title: '외국어 음성 지원 서비스(코스안내) 제공',
    content: [
      {
        content: '영어',
      },
    ],
    prevChar: '-',
  };

export const ROAD_NOTICE_VIDEOS: readonly ExamNoticeVideoItem[] = [
  {
    title: '시험안내 동영상',
    caption: '도로주행 시험안내 (2025. 8. 25.)',
    youtubeVideoId: 'rFXW1NYBxaE',
  },
] as const;

export const WRITTEN_NOTICE_COMMON_SECTIONS: readonly WrittenNoticeCommonSection[] =
  [
    {
      title: '시험 내용',
      content: [
        {
          content:
            '안전운전에 필요한 교통법규 등 공개된 학과시험 문제은행 중 40문제 출제',
        },
      ],
      highlight: ['40문제 출제'],
    },
    {
      title: '준비물',
      content: [
        {
          content: '응시원서(신체검사 완료 또는 건강검진결과서 조회 및 제출)',
        },
        {
          content:
            '신청일로부터 6개월 내에 모자를 벗은 상태에서 배경 없이 촬영된 3.5 x 4.5cm 규격의 상반신 컬러사진(허용되는 사진규격) 3매',
        },
        {
          content: '신분증(신분증 인정 범위)',
        },
      ],
      prevChar: '-',
    },
    {
      title: '결과발표',
      content: [
        {
          content: '시험 종료 즉시 컴퓨터 모니터에 획득 점수 및 합격 여부 표시',
        },
        {
          content:
            '합격 또는 불합격 도장이 찍힌 응시원서를 돌려받아 본인이 보관',
        },
      ],
      prevChar: '-',
    },
    {
      title: '주의사항',
      content: [
        {
          content:
            '학과시험 최초 응시일로부터 1년 이내 학과시험에 합격하여야 함',
        },
        {
          content: '학과시험 합격일로부터 1년 이내 기능시험에 합격하여야 함',
        },
        {
          content:
            '1년경과 시 기존 원서 폐기 후 학과시험부터 신규 접수하여야 하며 이때 교통안전교육 재수강은 불필요',
        },
      ],
      highlight: [
        '1년 이내 학과시험에 합격',
        '1년 이내 기능시험에 합격',
        '교통안전교육 재수강은 불필요',
      ],
      prevChar: '-',
    },
    {
      title: '비문해자를 위한 PC학과시험',
      content: [
        {
          content:
            '시험문제와 보기를 음성으로 들을 수 있는 PC학과시험 시험시간 총 80분, 민원실에서 접수 시 신청가능',
        },
      ],
      prevChar: '-',
    },
    {
      title: '청각장애인을 위한 수화 PC학과시험',
      content: [
        {
          content:
            '청각장애인이면서 비문해자를 위한 수화로 보는 PC학과시험 시험기간 총 80분, 민원실에서 접수 시 신청 가능',
        },
      ],
      prevChar: '-',
    },
  ] as const;

/** 증명사진 규격 이미지 박스 비율(가로·세로, 예: 스펙 캔버스 318×129) */
export type WrittenPhotoSpecImageAspect = readonly [
  width: number,
  height: number,
];

/**
 * 인라인 강조 — `string`은 줄 안에서 기본 굵기, 객체는 `emphasis`만큼 더 두껍게.
 * @example 한 줄에 섞기: `[{ text: '주민등록증', emphasis: 'bold' }, '(나머지…)']`
 * @example 줄 전체가 강조만: `{ text: '제목만 굵게', emphasis: 'bold' }`
 */
export type WrittenNoticeSheetTextPart =
  | string
  | {
      readonly text: string;
      readonly emphasis?: 'bold' | 'extrabold';
    };

/** 한 줄: 평문 또는 `TextPart` 배열(구간별 굵기) */
export type WrittenNoticeSheetRichLine =
  string | readonly WrittenNoticeSheetTextPart[];

/** `{ text, emphasis }` 만으로 한 줄 전체가 강조일 때(배열로 감쌀 필요 없음) */
export type WrittenNoticeSheetEmphasisLine = Exclude<
  WrittenNoticeSheetTextPart,
  string
>;

/** 불릿 한 줄(`string` / RichLine / 강조 한 줄) 또는 본문(`line`) + 하위 줄(`subLines`, ·) */
export type WrittenNoticeSheetBullet =
  | WrittenNoticeSheetRichLine
  | WrittenNoticeSheetEmphasisLine
  | {
      readonly line: WrittenNoticeSheetRichLine;
      readonly subLines?: readonly WrittenNoticeSheetRichLine[];
    };

export type WrittenNoticeSheetSectionImage = {
  readonly type: 'image';
  title: string;
  headerSub?: string;
  imageSrc: string;
  imageAspect: WrittenPhotoSpecImageAspect;
  bullets: readonly WrittenNoticeSheetBullet[];
};

export type WrittenNoticeSheetSectionText = {
  readonly type: 'text';
  title: string;
  headerSub?: string;
  bullets: readonly WrittenNoticeSheetBullet[];
};

export type WrittenNoticeSheetSection =
  WrittenNoticeSheetSectionImage | WrittenNoticeSheetSectionText;

/** @deprecated `WrittenNoticeSheetSectionImage` 사용 */
export type WrittenPhotoSpecSection = WrittenNoticeSheetSectionImage;

export const WRITTEN_PHOTO_SPEC_SECTIONS: readonly WrittenNoticeSheetSectionImage[] =
  [
    {
      type: 'image',
      title: '표준사진',
      imageSrc: '/images/exam/written/photo_spec_01_1.webp',
      imageAspect: [318, 129] as const,
      bullets: [
        '3.5cm x 4.5cm 탈모, 상반신, 흰색 배경, 6개월 이내 촬영한 컬러사진(사진인화용지에 인화된 사진만 허용)',
        '복사, 포토샵 등으로 수정하지 않은 사진',
        '얼굴 방향은 정면을 응시하고 기울어지지 않은 사진',
        '색안경 착용 등 눈을 가리지 않은 사진',
      ],
    },
    {
      type: 'image',
      title: '사진품질',
      imageSrc: '/images/exam/written/photo_spec_02_1.webp',
      imageAspect: [318, 119] as const,
      bullets: [
        '사진 인화용지에 출력한 컬러사진',
        '화상자료 입력 용도로 부적합한 사진 사용금지',
        '훼손 및 오염 되거나 화질이 흐린 사진 사용금지',
      ],
    },
    {
      type: 'image',
      title: '배경 및 품질',
      imageSrc: '/images/exam/written/photo_spec_03_1.webp',
      imageAspect: [318, 98] as const,
      bullets: [
        '무배경, 적절한 조명 사용하여 사진 촬영',
        '기준에 맞지 않은 조명 사용 시 반려될 수 있음',
      ],
    },
    {
      type: 'image',
      title: '장신구 등 착용',
      imageSrc: '/images/exam/written/photo_spec_04_1.webp',
      imageAspect: [318, 100] as const,
      bullets: [
        '액세서리 착용으로 인한 과도한 얼굴 가림이 없어야 함',
        '인위적으로 합성된 사진 불가',
      ],
    },
    {
      type: 'image',
      title: '얼굴 방향 등',
      imageSrc: '/images/exam/written/photo_spec_05_1.webp',
      imageAspect: [318, 100] as const,
      bullets: [
        '정면 구도, 정면 응시, 무왜곡된 사진만 허용',
        '표정은 무표정을 유지하며, 치아가 보이지 않아야 함',
      ],
    },
    {
      type: 'image',
      title: '홈페이지 이용 시',
      headerSub: '* 면허 소지자 홈페이지 접수 시',
      imageSrc: '/images/exam/written/photo_spec_06_1.webp',
      imageAspect: [318, 232] as const,
      bullets: [
        '250kb(킬로바이트) 이하의 jpg 파일',
        '가로 350px(픽셀) x 세로 450px(픽셀)',
        '표준규격을 준수할 것',
      ],
    },
  ];

/** 준비물 > 신분증 인증 범위 BottomSheet 본문 */
export const WRITTEN_ID_VERIFICATION_SECTIONS: readonly WrittenNoticeSheetSectionText[] =
  [
    {
      type: 'text',
      title: '증명서 자체만으로 신분확인이 가능한 신분증명서 등',
      bullets: [
        [
          { text: '주민등록증', emphasis: 'extrabold' },
          '(사진에 간인 후 주요 기재사항에 테이프가 부착된 주민등록증 발급신청 확인서), 재외국민 주민등록증, 운전면허증, 여권, 청소년증(유효기간 이내)',
        ],
        {
          line: [
            {
              text: '사진, 생년월일, 성명이 기재된 여타 공공기관에서 발행된 신분증 등',
              emphasis: 'extrabold',
            },
          ],
          subLines: [
            '예) 공무원증, 선원수첩, 국가기술자격증, 학생증(재학증명서 첨부), 장애인복지카드, 전역증(전역 후 1년 이내), 외국인등록증, 외국국적 동포 국내거소신고증, 국가보훈처장(證) → 국가유공자증 등 10종',
          ],
        },
        {
          line: [
            {
              text: '교정공무원이 수용자를 대리할 경우 교정기관장이 발행한 수용 증명서',
              emphasis: 'extrabold',
            },
          ],
          subLines: [
            '사진, 생년월일, 성명이 명확하게 기재되어 있고 공공기관의 장이 발행한 것에 한함',
            '유효기간이 명시되어 있는 신분증명서인 경우 유효기간이 경과하지 아니할 것(여권, 주민등록증 발급신청 확인서 등)',
            '상기 제출한 신분증 상의 사진이 본인과 현저히 다르거나 손괴되어 본인 확인이 곤란한 경우 신분확인이 가능한 여타 신분증명서 등을 제출하도록 하되, 본인이 동의하는 경우 지문 조회 가능',
          ],
        },
      ],
    },
    {
      type: 'text',
      title: '추가 증빙자료가 필요한 신분증의 종류 및 신분확인 절차',
      bullets: [
        '사진, 생년월일, 이름이 식별 곤란하거나 동일인 여부가 의심되는 경우',
        '신분확인이 가능한 신분증명서 등을 제출토록 하되, 사진, 생년월일, 이름의 식별이 어렵거나 위조 의심, 사진이 본인과 다른 사람으로 판단되는 경우 등 본인 확인이 어려운 경우 본인의 동의를 얻어 지문 조회 등의 방법으로 신분확인',
        '주민등록증이 발급되지 아니하여 지문 정보가 없는 미성년자가 원동기장치자전거 면허시험에 응시하는 경우',
        {
          line: '사진, 생년월일, 이름이 식별 곤란하거나 동일인 여부가 의심되어 지문 조회하였음에도 지문 훼손 또는 전산상 지문 누락으로 확인되지 않는 경우',
          subLines: [
            '사진, 생년월일, 이름이 기재된 신분증 외에 주민등록번호 뒷자리를 확인할 수 있는 국가기관 발행의 증빙 자료를 제출받아 신분확인',
          ],
        },
        '신분증명서 미 소지자가 지문 조회만으로 신분확인 불가',
      ],
    },
  ];

export const WRITTEN_PHOTO_SPEC_FOOTER =
  '* 제출한 사진이 사진규격에 부합하지 않은 경우 면허증 발급이 지연 및 반려될 수 있음';

/** 학과시험 > 운플 활용법 — 단계 카드(이미지는 `public/images/exam/written/usage_step_*.webp`) */
export interface WrittenExamUsageStep {
  /** 표시용 순번 (예: 1 → "Step 01") */
  readonly step: number;
  readonly title: string;
  readonly imageSrc: string;
  readonly content: string;
  /** 보조 문단(줄바꿈 가능) */
  readonly subContent?: string;
}

export const WRITTEN_EXAM_USAGE_STEPS: readonly WrittenExamUsageStep[] = [
  {
    step: 1,
    title: '모의고사 40제',
    imageSrc: '/images/exam/written/usage_step_01.webp',
    content:
      '실제 시험처럼 시간에 맞춰 모의고사 형식으로 문제를 풀면서 본인의 실력을 확인하며 학습할 수 있어요.',
  },
  {
    step: 2,
    title: '유형별 학습하기',
    imageSrc: '/images/exam/written/usage_step_02.webp',
    content:
      '유형별 학습하기 메뉴에서 각 유형의 문제들을 쉽고 빠르게 공부할 수 있어요.',
    subContent:
      '문장형(21문제), 사진형(8문제), 일러스트형(7문제), 안전표지형(5문제), 동영상형(1문제)',
  },
  {
    step: 3,
    title: '급할때 200문제',
    imageSrc: '/images/exam/written/usage_step_03.webp',
    content:
      '시험 직전에 지금까지 틀린 문제들만 모아 놓은 오답노트를 중점적으로 확인하세요!',
  },
  {
    step: 4,
    title: '복습하기',
    imageSrc: '/images/exam/written/usage_step_04.webp',
    content:
      '복습하기 메뉴에서 지금까지 틀린 문제들과 즐겨찾기로 정리해 놓은 문제들을 한눈에 확인할 수 있어요.',
  },
];

export const WRITTEN_EXAM_LICENSE_LIST: WrittenExamLicenseEntity[] = [
  {
    sequence: 1,
    licenseType: '2종 보통',
    passingCriteria: '60점 이상 시 합격',
    examEligibility: '18세 이상 부터 응시가능',
    fee: 10000,
    iconUrl: '/images/exam/icon_class_2.svg',
  },
  {
    sequence: 2,
    licenseType: '1종 보통',
    passingCriteria: '70점 이상 시 합격',
    examEligibility: '18세 이상 부터 응시가능',
    fee: 10000,
    iconUrl: '/images/exam/icon_class_1.svg',
  },
  {
    sequence: 1,
    licenseType: '1종 대형',
    subLicenseType: '(대형견인,소형견인,구난차)',
    passingCriteria: '70점 이상 시 합격',
    examEligibility: '19세 이상,\n1ㆍ2종 보통면허 취득\n1년 경과 후 응시가능',
    fee: 10000,
    iconUrl: '/images/exam/icon_class_1_big.svg',
  },
  {
    sequence: 4,
    licenseType: '2종 소형',
    subLicenseType: '(125cc 초과 이륜자동차)',
    passingCriteria: '60점 이상 시 합격',
    examEligibility: '18세 이상 부터 응시가능',
    fee: 10000,
    iconUrl: '/images/exam/icon_class_2_small.svg',
  },
  {
    sequence: 5,
    licenseType: '2종 원동기',
    subLicenseType: '(125cc 이하)',
    passingCriteria: '60점 이상 시 합격',
    examEligibility: '16세 이상 부터 응시가능',
    fee: 8000,
    iconUrl: '/images/exam/icon_class_2_motorcycle.svg',
  },
] as const;
